// Generated by scripts/build-catalog.mjs. Edit scripts/catalog/*.js.
// Shared catalog transport. UI adapters must never filter or sort a partial page.
const cache = new Map();
const lifetime = 15000;

export function contextKey() {
  return [document.documentElement.lang, window.Shopify?.country,
    window.Shopify?.currency?.active, window.Shopify?.routes?.root].join('|');
}

export function localUrl(value, origin = window.location.origin) {
  const url = new URL(value, origin);
  if (url.origin !== origin) throw new Error('Unexpected catalog origin');
  url.hash = '';
  return url;
}

export function filterKind(key) {
  if (!key.startsWith('filter.')) return '';
  if (/sale_status|salestatus|sale-status/.test(key) || key === 'filter.v.availability') return 'availability';
  if (/ipinfo|ip_info/.test(key)) return 'series';
  if (/character/.test(key)) return 'character';
  if (/category|categories/.test(key)) return 'category';
  return '';
}

export function hasActiveFilter(query, kind) {
  return [...query.searchParams].some(([key, value]) => filterKind(key) === kind && value !== '');
}

export function changeFilter(current, optionUrl, kind, origin = window.location.origin) {
  const url = localUrl(current, origin);
  for (const key of [...url.searchParams.keys()]) {
    if (filterKind(key) === kind || key === 'page') url.searchParams.delete(key);
  }
  if (optionUrl) {
    const selected = localUrl(optionUrl, origin);
    for (const [key, value] of selected.searchParams) {
      // Existing UI is single-select. Shopify url_to_add can include the old
      // value followed by the new one; retain only the final value per key.
      if (filterKind(key) === kind) url.searchParams.set(key, value);
    }
  }
  return url;
}

export function requestUrl(value, pageSize, origin = window.location.origin) {
  const url = localUrl(value, origin);
  url.searchParams.delete('section_id');
  url.searchParams.delete('view');
  url.searchParams.set('sections', pageSize === 12 ? 'vibemax-online-products-fragment' : 'vibemax-catalog-fragment');
  return url;
}

export function nextPageUrl(current, next, origin = window.location.origin) {
  if (!next) return null;
  const url = localUrl(current, origin);
  const page = Number(localUrl(next, origin).searchParams.get('page'));
  if (!Number.isInteger(page) || page <= Number(url.searchParams.get('page') || 1)) {
    throw new Error('Catalog pagination did not advance');
  }
  url.searchParams.set('page', String(page));
  return url;
}

export function parseCatalog(text, parse = (html) => new DOMParser().parseFromString(html, 'text/html')) {
  let html = text;
  if (text.trim().startsWith('{')) {
    const payload = JSON.parse(text);
    html = payload['vibemax-catalog-fragment'] ?? payload['vibemax-online-products-fragment'];
    if (typeof html !== 'string') throw new Error('Catalog section unavailable');
  }
  const doc = parse(html);
  const root = doc.querySelector('[data-vmax-online-products-fragment]');
  if (!root?.querySelector('.vmax-online-products__grid') || !/^\d+$/.test(root.dataset.catalogTotal || '')) throw new Error('Catalog results unavailable');
  return root;
}

export class CatalogRequest {
  constructor(fetcher = (...args) => fetch(...args), parse = parseCatalog, getContext = contextKey) {
    this.fetcher = fetcher;
    this.parse = parse;
    this.getContext = getContext;
    this.sequence = 0;
    this.requests = 0;
  }
  cancel() {
    this.sequence++;
    this.controller?.abort();
    this.pending = null;
  }
  load(url) {
    const key = `${this.getContext()}|${url}`;
    if (this.pending?.key === key) return this.pending.promise;
    this.cancel();
    const sequence = this.sequence;
    const controller = this.controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    const promise = (async () => {
      // Let pending be assigned even when serving a synchronous cache hit.
      await Promise.resolve();
      try {
        let entry = cache.get(key);
        if (!entry || Date.now() - entry.at > lifetime) {
          this.requests++;
          const response = await this.fetcher(String(url), {
            credentials: 'same-origin', signal: controller.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error(`Catalog request failed: ${response.status}`);
          const text = await response.text();
          const result = this.parse(text); // Never cache a 200/null or malformed result.
          if (sequence !== this.sequence) return null;
          cache.set(key, { text, at: Date.now() });
          while (cache.size > 6) cache.delete(cache.keys().next().value);
          return result;
        }
        return sequence === this.sequence ? this.parse(entry.text) : null;
      } catch (error) {
        if (sequence !== this.sequence) return null;
        throw error;
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.pending = null;
      }
    })();
    this.pending = { key, promise };
    return promise;
  }
}


const mobile = window.matchMedia('(max-width: 749px)');
const controllers = new Map();
let active;
let activeChannel;
let restoring = false;
const publicUrl = location.href;
const storageKey = `vmax-catalog:${contextKey()}:${publicUrl}`;
let saved = {};
try {
  const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
  if (stored && Date.now() - stored.at < 1800000) saved = stored.state;
} catch { /* Storage is optional. */ }
saved = history.state?.vmaxCatalog || saved;

const labelOf = (option) => option?.querySelector('span:last-child')?.textContent.trim() || option?.textContent.trim() || '';
const announce = (root, text) => {
  let status = root.querySelector('[data-vmax-catalog-status]');
  if (!status) {
    status = document.createElement('span');
    status.className = 'visually-hidden';
    status.dataset.vmaxCatalogStatus = '';
    status.setAttribute('role', 'status');
    root.append(status);
  }
  status.textContent = text;
};

function remember(push = false) {
  if (restoring) return;
  const state = { ...saved, active: activeChannel || active?.id };
  controllers.forEach((controller, id) => { state[id] = controller.committed.toString(); });
  saved = state;
  // Keep all customer-facing URLs unchanged, including their existing query/hash.
  history[push ? 'pushState' : 'replaceState']({ ...history.state, vmaxCatalog: state }, '', location.href);
  try { sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), state })); } catch { /* optional */ }
}

function sameSelection(optionUrl, query, kind) {
  const pairs = [...localUrl(optionUrl || query).searchParams].filter(([key]) => filterKind(key) === kind);
  const current = [...query.searchParams].filter(([key]) => filterKind(key) === kind);
  return pairs.length === current.length && pairs.every(([key, value]) => query.searchParams.getAll(key).includes(value));
}

function fieldsFor(toolbar, feed) {
  return ['series', 'character', 'category', 'availability'].map((kind) => {
    const mainName = kind === 'category' ? 'categories' : kind;
    const optionSelector = feed ? `[data-vmax-feed-${kind}-option]` : `[data-vmax-${mainName}-option]`;
    const first = toolbar.querySelector(optionSelector);
    const dropdown = feed ? first?.closest('[data-vmax-feed-dropdown]') : toolbar.querySelector(`[data-vmax-${mainName}-dropdown]`);
    return {
      kind, mainName, optionSelector, dropdown,
      panel: dropdown?.querySelector('[role="listbox"]'),
      trigger: dropdown?.querySelector('button[aria-haspopup]'),
      label: dropdown?.querySelector(feed ? '[data-vmax-feed-label]' : `[data-vmax-${mainName}-label]`),
      select: toolbar.querySelector(feed ? `[data-vmax-feed-${kind}-select]` : `[data-vmax-online-${kind === 'category' ? 'categories' : kind}-filter]`),
      all: first?.cloneNode(true)
    };
  });
}

class Catalog {
  constructor({ id, root, grid, toolbar, collection, feed = false, rail = false }) {
    Object.assign(this, { id, root, grid, toolbar, collection, feed, rail });
    this.pageSize = !feed && !rail ? 12 : 24;
    this.transport = new CatalogRequest();
    this.moreTransport = new CatalogRequest();
    this.fields = fieldsFor(toolbar, feed);
    this.facets = null;
    this.query = localUrl(collection);
    for (const [key, value] of new URL(publicUrl).searchParams) {
      if (key.startsWith('filter.') || ['sort_by', 'page', 'preview_theme_id', '_fd', 'pb'].includes(key)) this.query.searchParams.append(key, value);
    }
    this.query.searchParams.set('sort_by', this.query.searchParams.get('sort_by') || 'created-descending');
    if (saved[id]) {
      try {
        const candidate = localUrl(saved[id]);
        if (candidate.pathname === this.query.pathname) this.query = candidate;
      } catch { /* Ignore invalid saved state. */ }
    }
    this.committed = new URL(this.query);
    this.root.dataset.vmaxCatalogOwned = '';
    this.root.addEventListener('click', (event) => {
      const pagination = event.target.closest('.vmax-progressive-pagination a');
      if (pagination && this.root.contains(pagination)) {
        event.preventDefault();
        const query = new URL(this.query);
        query.searchParams.set('page', localUrl(pagination.href).searchParams.get('page') || '1');
        this.load(query, { push: true });
      }
      if (event.target.closest('.vmax-mobile-infinite__button, .vmax-online-channel-loader__button')) {
        event.preventDefault();
        if (this.failedQuery) this.load(this.failedQuery, { push: true });
        else this.loadMore();
      }
    });
  }

  optionUrl(option) { return option?.dataset.nativeFilterUrl || option?.dataset.url || ''; }

  busy(value) {
    this.loading = value;
    if (value) this.root.setAttribute('aria-busy', 'true');
    else this.root.removeAttribute('aria-busy');
    const indicator = this.root.querySelector('[data-vmax-online-products-loading]');
    indicator?.classList.toggle('is-visible', value);
    indicator?.setAttribute('aria-hidden', String(!value));
    const notice = document.querySelector('[data-vmax-online-loading-notice]');
    if (notice && active === this && mobile.matches) {
      notice.hidden = !value;
      notice.classList.toggle('is-visible', value);
    }
    const loader = this.loader();
    loader?.classList.toggle('is-loading', value);
    if (value) announce(this.root, 'Loading products');
  }

  async load(value = this.query, { push = false } = {}) {
    push = push && !restoring;
    const query = localUrl(value);
    if (this.loading && query.toString() === this.query.toString()) return;
    this.query = query;
    this.moreTransport.cancel();
    this.loadingMore = false;
    this.observer?.disconnect();
    this.busy(true);
    const version = this.version = (this.version || 0) + 1;
    try {
      const result = await this.transport.load(requestUrl(query, this.pageSize));
      if (!result || version !== this.version) return;
      nextPageUrl(query, result.dataset.catalogNext); // Validate before replacing cards.
      this.failedQuery = null;
      this.committed = new URL(query);
      this.facets = [...result.querySelectorAll('[data-vmax-online-feed-native-facet]')].map((item) => ({ ...item.dataset }));
      this.total = Number(result.dataset.catalogTotal);
      this.renderCards(result, false);
      this.renderPagination(result);
      this.loaded = true;
      if (active === this && !this.draft) this.renderControls();
      announce(this.root, `${this.total} products`);
      remember(push);
    } catch (error) {
      if (version !== this.version) return;
      this.failedQuery = new URL(query);
      this.query = new URL(this.committed);
      if (active === this && !this.draft) this.renderControls();
      this.setLoaderState('error', 'Couldn’t load products. Please try again.');
      announce(this.root, 'Couldn’t load products. Select your filter again to retry.');
    } finally {
      if (version === this.version) {
        this.root.dataset.vmaxCatalogRequests = this.transport.requests + this.moreTransport.requests;
        this.busy(false);
        if (!this.failedQuery) this.observe();
      }
    }
  }

  renderCards(result, append) {
    const cards = [...result.querySelectorAll('.vmax-online-products__grid .vmax-online-product[data-vmax-product-id]')];
    const fragment = document.createDocumentFragment();
    const existing = append ? new Set([...this.grid.querySelectorAll('[data-vmax-product-id]')].map((card) => card.dataset.vmaxProductId)) : new Set();
    for (const card of cards) {
      if (existing.has(card.dataset.vmaxProductId)) continue;
      existing.add(card.dataset.vmaxProductId);
      if (this.feed) {
        const wrapper = document.createElement('div');
        wrapper.className = 'vmax-online-feed__product';
        wrapper.dataset.vmaxFeedProduct = '';
        wrapper.dataset.productId = card.dataset.vmaxProductId;
        wrapper.append(card);
        fragment.append(wrapper);
      } else fragment.append(card);
    }
    if (!append) {
      if (!cards.length) {
        const empty = result.querySelector('.vmax-online-products__fragment-empty')?.cloneNode(true);
        if (empty) fragment.append(empty);
      }
      // Feed pagination lives inside its grid; it is rebuilt from this result.
      this.grid.replaceChildren(fragment);
    } else {
      const pagination = this.grid.querySelector('.vmax-progressive-pagination');
      if (pagination) this.grid.insertBefore(fragment, pagination);
      else this.grid.append(fragment);
    }
    document.dispatchEvent(new CustomEvent('vmax:translatable-content-updated'));
  }

  renderPagination(result, query = this.query) {
    // Take the page number only: the current query owns all filters/sort.
    this.next = nextPageUrl(query, result.dataset.catalogNext);
    if (!this.rail) {
      this.root.querySelector('.vmax-progressive-pagination')?.remove();
      const pagination = result.querySelector('.vmax-progressive-pagination')?.cloneNode(true);
      if (pagination) {
        pagination.classList.toggle('vmax-online-products__pagination', !this.feed);
        pagination.classList.toggle('vmax-online-feed__pagination', this.feed);
        // Keep the pagination component's responsive styles when replacing the
        // section-rendered fragment. Remove scripts to avoid re-executing the
        // snippet, but removing the style would expose the mobile-only loader
        // on desktop after every catalog refresh.
        pagination.querySelectorAll('script').forEach((node) => node.remove());
        pagination.querySelectorAll('a').forEach((link) => {
          const destination = new URL(publicUrl);
          destination.searchParams.set('page', localUrl(link.getAttribute('href')).searchParams.get('page') || '1');
          link.setAttribute('href', `${destination.pathname}${destination.search}${destination.hash}`);
        });
        if (this.feed) this.grid.append(pagination);
        else this.grid.after(pagination);
      }
    }
    this.setLoaderState(this.next ? 'idle' : 'complete', this.next ? '' : 'You’ve reached the end');
  }

  loader() { return this.root.querySelector(this.rail ? '[data-vmax-online-channel-loader]' : '[data-vmax-mobile-infinite]'); }
  setLoaderState(state, message) {
    const loader = this.loader();
    if (!loader) return;
    loader.hidden = false;
    loader.classList.remove('is-idle', 'is-loading', 'is-error', 'is-complete', 'is-manual');
    loader.classList.add(`is-${state}`);
    const status = loader.querySelector('.vmax-mobile-infinite__status, .vmax-online-channel-loader__status');
    if (status) status.textContent = message;
    const button = loader.querySelector('button');
    if (button) button.textContent = state === 'error' ? 'Try again' : 'Load more';
  }

  observe() {
    this.observer?.disconnect();
    const loader = this.loader();
    loader?.closest('.vmax-progressive-pagination')?.classList.toggle('is-mobile-enhanced', mobile.matches);
    if (!mobile.matches || active !== this || !this.next || this.loading || this.failedQuery) return;
    if (navigator.connection?.saveData || !('IntersectionObserver' in window)) {
      this.setLoaderState('manual', '');
      return;
    }
    const sentinel = loader?.querySelector('[data-vmax-mobile-infinite-sentinel], [data-vmax-online-channel-sentinel]');
    if (!sentinel) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
    }, { rootMargin: '900px 0px 900px' });
    this.observer.observe(sentinel);
  }

  async loadMore() {
    if (!this.next || this.loading || this.loadingMore || active !== this) return;
    this.loadingMore = true;
    this.observer?.disconnect();
    this.setLoaderState('loading', 'Loading more');
    const next = new URL(this.next);
    const version = this.version;
    try {
      const result = await this.moreTransport.load(requestUrl(next, this.pageSize));
      if (!result || version !== this.version) return;
      nextPageUrl(next, result.dataset.catalogNext);
      this.renderCards(result, true);
      this.renderPagination(result, next);
    } catch {
      if (version === this.version) this.setLoaderState('error', 'Couldn’t load more products');
      return; // Explicit retry; never loop automatically on failures.
    } finally {
      this.root.dataset.vmaxCatalogRequests = this.transport.requests + this.moreTransport.requests;
      if (version === this.version) this.loadingMore = false;
    }
    if (version === this.version) this.observe();
  }

  renderControls() {
    const query = this.draft || this.query;
    for (const field of this.fields) {
      if (!field.panel || !field.all) continue;
      if (this.facets) {
        const fragment = document.createDocumentFragment();
        const all = field.all.cloneNode(true);
        if (this.feed) all.dataset.nativeFilterUrl = '';
        else all.dataset.url = '';
        fragment.append(all);
        const facets = this.facets.filter((facet) => facet.filterType === field.kind);
        for (const facet of facets) {
          const option = field.all.cloneNode(true);
          option.classList.remove('is-selected');
          option.setAttribute('aria-selected', 'false');
          if (this.feed) {
            option.dataset.filterValue = facet.filterValue;
            option.dataset.nativeFilterUrl = facet.nativeFilterUrl;
          } else {
            option.dataset[`${field.mainName}Key`] = facet.filterValue;
            option.dataset.url = facet.nativeFilterUrl;
          }
          const label = option.querySelector('span:last-child');
          if (label) {
            label.textContent = facet.filterLabel;
            if (['series', 'character'].includes(field.kind)) {
              label.classList.add('notranslate');
              label.setAttribute('translate', 'no');
              label.dataset.vmaxNoTranslate = '';
              label.setAttribute(field.kind === 'series' ? 'data-vmax-ip-name' : 'data-vmax-character-name', '');
            }
          }
          fragment.append(option);
        }
        // Retain an active zero-result selection even when Shopify omits its facet.
        const previous = hasActiveFilter(query, field.kind) && [...field.panel.querySelectorAll(field.optionSelector)].find((option) => this.optionUrl(option) && sameSelection(this.optionUrl(option), query, field.kind));
        if (previous && ![...fragment.children].some((option) => this.optionUrl(option) && sameSelection(this.optionUrl(option), query, field.kind))) fragment.append(previous.cloneNode(true));
        field.panel.replaceChildren(fragment);
      }
      const options = [...field.panel.querySelectorAll(field.optionSelector)];
      const hasFilter = [...query.searchParams.keys()].some((key) => filterKind(key) === field.kind);
      let selected = options.find((option) => this.optionUrl(option) && sameSelection(this.optionUrl(option), query, field.kind));
      if (!hasFilter) selected = options[0];
      selected ||= options.find((option) => option.classList.contains('is-selected')) || options[0];
      for (const option of options) {
        option.classList.toggle('is-selected', option === selected);
        option.setAttribute('aria-selected', String(option === selected));
      }
      if (field.label) field.label.textContent = labelOf(selected);
      if (field.select) {
        field.select.replaceChildren(...options.map((option) => {
          const native = new Option(labelOf(option), this.optionUrl(option));
          native.selected = option === selected;
          return native;
        }));
      }
    }
    const sortOptions = [...this.toolbar.querySelectorAll(this.feed ? '[data-vmax-feed-sort-option]' : '[data-vmax-sort-option]')];
    for (const option of sortOptions) {
      const selected = (option.dataset.sortValue || option.dataset.filterValue) === query.searchParams.get('sort_by');
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', String(selected));
      if (selected) {
        const label = this.toolbar.querySelector(this.feed ? '.vmax-online-feed__dropdown--sort [data-vmax-feed-label]' : '[data-vmax-sort-label]');
        if (label) label.textContent = labelOf(option);
      }
    }
    this.renderChips(query);
  }

  renderChips(query) {
    const container = this.toolbar.querySelector('[data-vmax-mobile-filter-chips]');
    if (!container) return;
    const selected = this.fields.filter((field) => [...query.searchParams.keys()].some((key) => filterKind(key) === field.kind));
    const count = this.toolbar.querySelector('[data-vmax-mobile-filter-count]');
    if (count) { count.hidden = !selected.length; count.textContent = selected.length ? `· ${selected.length}` : ''; }
    container.hidden = !selected.length;
    container.replaceChildren();
    for (const field of selected) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'vmax-online-products__mobile-filter-chip';
      chip.textContent = `${field.label?.textContent || field.kind} ×`;
      chip.setAttribute('aria-label', `Remove ${field.label?.textContent || field.kind} filter`);
      chip.addEventListener('click', () => this.load(changeFilter(this.query, '', field.kind), { push: true }));
      container.append(chip);
    }
    if (selected.length) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'vmax-online-products__mobile-filter-chip is-clear';
      clear.textContent = 'Clear';
      clear.addEventListener('click', () => this.reset());
      container.append(clear);
    }
    const apply = this.toolbar.querySelector('[data-vmax-mobile-filter-apply-label]');
    if (apply) apply.textContent = 'View results';
    this.toolbar.querySelector('[data-vmax-mobile-filter-apply]')?.setAttribute('aria-label', `View results${Number.isFinite(this.total) && !this.draft ? `, ${this.total} products` : ''}`);
  }

  reset() {
    const query = new URL(this.draft || this.query);
    for (const key of [...query.searchParams.keys()]) if (key.startsWith('filter.') || key === 'page') query.searchParams.delete(key);
    if (this.draft) { this.draft = query; this.renderControls(); }
    else this.load(query, { push: true });
  }
}

function bindToolbar(toolbar, feed) {
  const dropdownSelector = feed ? '[data-vmax-feed-dropdown]' : '.vmax-online-products__dropdown';
  const close = () => {
    toolbar.querySelectorAll(dropdownSelector).forEach((dropdown) => {
      dropdown.classList.remove('is-open');
      dropdown.querySelector('button[aria-haspopup]')?.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('vmax-mobile-sort-open');
  };
  const drawer = toolbar.querySelector('[data-vmax-mobile-filter-drawer]');
  const closeDrawer = (cancel = true) => {
    drawer?.classList.remove('is-mobile-open');
    document.body.classList.remove('vmax-mobile-filter-open');
    toolbar.querySelector('[data-vmax-mobile-filter-open]')?.setAttribute('aria-expanded', 'false');
    if (cancel && active) { active.draft = null; active.renderControls(); }
    close();
  };
  toolbar.addEventListener('click', (event) => {
    if (!active) return;
    const target = event.target;
    if (target.closest('[data-vmax-mobile-filter-open]')) {
      active.draft = new URL(active.query);
      drawer.classList.add('is-mobile-open');
      document.body.classList.add('vmax-mobile-filter-open');
      toolbar.querySelector('[data-vmax-mobile-filter-open]').setAttribute('aria-expanded', 'true');
      active.fields[0]?.dropdown?.classList.add('is-open');
      active.fields[0]?.trigger?.setAttribute('aria-expanded', 'true');
      return;
    }
    if (target.closest('[data-vmax-mobile-filter-close]')) { closeDrawer(); return; }
    if (target.closest('[data-vmax-mobile-filter-reset]')) { active.reset(); return; }
    if (target.closest('[data-vmax-mobile-filter-apply]')) {
      const draft = active.draft;
      active.draft = null;
      closeDrawer(false);
      if (draft) active.load(draft, { push: true });
      return;
    }
    if (target.closest('[data-vmax-sort-close]')) { close(); return; }
    const trigger = target.closest('button[aria-haspopup="listbox"]');
    if (trigger) {
      const dropdown = trigger.closest(dropdownSelector);
      const opening = !dropdown?.classList.contains('is-open');
      close();
      dropdown?.classList.toggle('is-open', opening);
      trigger.setAttribute('aria-expanded', String(opening));
      if (trigger.matches('[data-vmax-sort-trigger]') && mobile.matches) document.body.classList.toggle('vmax-mobile-sort-open', opening);
      return;
    }
    const option = target.closest('button[role="option"]');
    if (!option) return;
    const field = active.fields.find((item) => option.matches(item.optionSelector));
    let query;
    if (field) query = changeFilter(active.draft || active.query, active.optionUrl(option), field.kind);
    else if (option.matches('[data-vmax-sort-option], [data-vmax-feed-sort-option]')) {
      query = new URL(active.query);
      query.searchParams.set('sort_by', option.dataset.sortValue || option.dataset.filterValue);
      query.searchParams.delete('page');
    }
    if (!query) return;
    if (active.draft && field) { active.draft = query; active.renderControls(); }
    else { close(); active.load(query, { push: true }); }
  });
  toolbar.addEventListener('change', (event) => {
    const field = active?.fields.find((item) => event.target === item.select);
    if (field) active.load(changeFilter(active.query, event.target.value, field.kind), { push: true });
  });
  document.addEventListener('click', (event) => { if (!toolbar.contains(event.target)) close(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawer(); });
}

export function bootFeed() {
  const root = document.querySelector('[data-vmax-feed-page]');
  if (!root || root.dataset.vmaxCatalogReady) return;
  root.dataset.vmaxCatalogReady = 'true';
  active = new Catalog({ id: 'feed', root, grid: root.querySelector('.vmax-online-feed__grid'), toolbar: root, collection: root.dataset.vmaxFeedCollectionUrl, feed: true });
  controllers.set('feed', active);
  bindToolbar(root, true);
  remember();
  active.load();
}

export function bootMain(config) {
  // The incumbent channel navigation selects its initial panel in rAF. Run
  // afterward so it cannot overwrite a restored history/session selection.
  window.requestAnimationFrame(() => initializeMain(config));
}

function initializeMain(config) {
  const toolbar = document.querySelector('[data-vmax-mobile-product-tools]');
  if (!toolbar || toolbar.dataset.vmaxCatalogReady) return;
  toolbar.dataset.vmaxCatalogReady = 'true';
  const anchor = document.createComment('vmax-catalog-tools');
  const allInner = document.querySelector('#vmax-online-products .vmax-online-store__inner');
  allInner.querySelector('[data-vmax-online-products-loading]').before(anchor);
  const activate = (id, push = false) => {
    if (active && active.id !== id) {
      active.observer?.disconnect();
      active.transport.cancel();
      active.moreTransport.cancel();
      active.version = (active.version || 0) + 1;
      active.query = new URL(active.committed);
      active.busy(false);
      active.loadingMore = false;
    }
    activeChannel = id;
    if (!config[id]) {
      active?.observer?.disconnect();
      active = null;
      const notice = document.querySelector('[data-vmax-online-loading-notice]');
      if (notice) { notice.hidden = true; notice.classList.remove('is-visible'); }
      remember(push);
      return;
    }
    active?.observer?.disconnect();
    const root = document.querySelector(`[data-vmax-channel-panel="${id}"]`);
    if (!root) return;
    let controller = controllers.get(id);
    if (!controller) {
      const rail = id !== 'all-products';
      controller = new Catalog({ id, root, grid: root.querySelector(rail ? '.vmax-new-drops-strip__rail, .vmax-ranking-deals__rail' : '.vmax-online-products__grid'), toolbar, collection: config[id], rail });
      controllers.set(id, controller);
    }
    active = controller;
    controller.draft = null;
    if (controller.loaded) { controller.renderControls(); controller.observe(); remember(push); }
    else controller.load(controller.query, { push });
  };
  bindToolbar(toolbar, false);
  document.addEventListener('vmax:channel-change', (event) => {
    if (mobile.matches) activate(event.detail.channel, true);
  });
  mobile.addEventListener('change', () => {
    if (!mobile.matches) { anchor.after(toolbar); activate('all-products'); }
    else {
      const id = document.documentElement.dataset.vmaxActiveChannel || 'new-drops';
      document.querySelector(`[data-vmax-channel-trigger="${id}"]`)?.click();
    }
  });
  let initial = mobile.matches ? (saved.active || document.documentElement.dataset.vmaxActiveChannel || 'new-drops') : 'all-products';
  if (!config[initial] && initial !== 'collections') initial = 'new-drops';
  if (mobile.matches) {
    restoring = true;
    document.querySelector(`[data-vmax-channel-trigger="${initial}"]`)?.click();
    restoring = false;
  }
  if (!active) activate(initial);
  remember();
}

window.addEventListener('popstate', (event) => {
  const state = event.state?.vmaxCatalog;
  if (!state) return;
  saved = state;
  restoring = true;
  if (mobile.matches && state.active) document.querySelector(`[data-vmax-channel-trigger="${state.active}"]`)?.click();
  controllers.forEach((controller, id) => {
    if (!state[id]) return;
    try {
      const query = localUrl(state[id]);
      if (query.pathname !== localUrl(controller.collection).pathname) return;
      if (controller === active) {
        if (controller.loaded && !controller.loading && query.toString() === controller.committed.toString()) {
          // A bfcache return already has the right cards, including appended
          // mobile pages. Do not throw them away or refetch the first page.
          controller.query = query;
          controller.renderControls();
          controller.observe();
        } else controller.load(query);
      }
      else {
        controller.transport.cancel();
        controller.moreTransport.cancel();
        controller.version = (controller.version || 0) + 1;
        controller.busy(false);
        controller.query = query;
        controller.committed = new URL(query);
        controller.loaded = false;
      }
    } catch { /* Ignore invalid history entries. */ }
  });
  restoring = false;
});
mobile.addEventListener('change', () => active?.observe());
